<script setup lang="ts">
import type { BannerComposition } from "~/models/generated/BannerComposition";

const props = defineProps<{
  content?: BannerComposition;
  heading?: string;
  subheading?: string;
  description?: string;
}>();

const button = computed( () => props.content?.bannerButton?.[0] );
</script>

<template>
  <section class="banner">
    <div class="container">
      <div class="banner__content">
        <h1 class="banner__heading">
          {{ heading || content?.bannerHeading }}
        </h1>
        <span
          v-if="subheading || content?.bannerSubheading"
          class="banner__subheading">
          {{ subheading || content?.bannerSubheading }}
        </span>
        <RichText
          v-if="description || content?.bannerDescription"
          class="banner__description"
          :content="description || content?.bannerDescription" />

        <div
          v-if="button && (button.route?.path || button.url)"
          class="banner__cta">
          <a-button
            :to="(button.route?.path || button.url) ?? undefined"
            :target="button.target ?? undefined"
            look="pill"
            suffix="fa:light:chevron-right"
            variant="primary">
            <span>{{ button.title }}</span>
          </a-button>
        </div>
      </div>

      <NuxtImg
        v-if="content?.bannerLogo?.length"
        class="banner__image"
        :src="content.bannerLogo[0].url"
        :alt="content.bannerLogo[0].properties.altText" />
    </div>
  </section>
</template>

<style scoped lang="scss">
.banner {
  background-color: var( --primary );
  color: var( --color-primary-text );

  .container {
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media ( max-width: 991px ) {
      flex-direction: column-reverse;
    }
  }

  &__content {
    padding: 60px 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    max-width: 40rem;

    @media ( max-width: 991px ) {
      padding-left: 0;
      padding-right: 0;
    }

    &:only-child {
      align-items: center;
      text-align: center;
      margin-left: auto;
      margin-right: auto;
    }
  }

  &__image {
    max-width: 100%;
    max-height: 300px;

    @media ( max-width: 991px ) {
      display: none;
    }
  }

  &__heading {
    padding-right: 20px;
    margin-bottom: 2rem;
    font-size: 3.5rem;
    font-weight: 500;
    order: 1;
    line-height: 1.1;

    @media ( max-width: 991px ) {
      font-size: 2.5rem;
    }
  }

  &__subheading {
    font-size: 1rem;
    order: 0;
    margin-bottom: 0;
    line-height: 1;
  }

  &__description {
    order: 2;
    max-width: 850px;
  }

  &__cta {
    order: 3;
  }

  &__subheading,
  &__description {
    color: #fff;
  }

  @media ( min-width: 768px ) {
    :deep( .banner-image__content ) {
      max-width: 50%;
      margin-left: auto;
      align-items: flex-start;
    }

    :deep( .banner-image__heading ) {
      max-width: none;
      text-align: left;
      padding-right: 0;
    }
  }

  &.home-page__banner {
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 450px;
    padding: 70px 0 40px;
    color: #fff;

    @media ( max-width: 991px ) {
      height: auto;
      padding: 20px 0;
    }

    .container {
      display: block;
      max-width: 1300px;
      margin: 0 auto;
      padding: 0;

      @media ( max-width: 991px ) {
        max-width: 100%;
        padding: 0 20px;
      }
    }

    .banner__content {
      padding: 0;
      align-items: flex-start;
      text-align: left;

      &:only-child {
        align-items: flex-start;
        text-align: left;
        margin-left: 0;
        margin-right: 0;
      }
    }

    .banner__heading {
      font-size: 4rem;
      font-weight: 400;
      order: 0;

      @media ( max-width: 991px ) {
        font-size: 3rem;
      }
    }

    .banner__subheading {
      order: 1;
    }

    .banner__image {
      position: absolute;
      right: 0;
      bottom: -20px;
      top: auto;
      max-width: 50%;
      max-height: 100%;
    }

    .banner__cta {
      margin-top: 20px;

      .btn.btn-primary.btn-pill {
        background: none;
        border: 1px solid #fff;
        color: #fff;

        &:hover {
          background: #E6007E;
          border: 1px solid #fff;
        }
      }
    }
  }
}
</style>
